import { APP_ROUTER } from '@/routes/routes';
import { Typography } from 'antd';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

// 色の意味（kazokugoto のフロー図と揃える）
export const FLOW_COLORS = {
  llm: { fill: '#f9f0ff', stroke: '#b37feb', text: '#531dab' }, // Anthropic
  search: { fill: '#fff7e6', stroke: '#ffc069', text: '#d46b08' }, // Tavily 検索
  local: { fill: '#fafafa', stroke: '#d9d9d9', text: '#595959' }, // ローカル処理
  db: { fill: '#e6f4ff', stroke: '#69b1ff', text: '#0958d9' }, // 保存
  deliver: { fill: '#f6ffed', stroke: '#95de64', text: '#389e0d' }, // 配信
};

export type FlowStepKind = keyof typeof FLOW_COLORS;

export type FlowStep = {
  kind: FlowStepKind;
  title: string;
  sub: string;
  /** クリックでプロンプト管理ページに飛ぶ agent_prompts の key */
  promptKeys?: string[];
  /** 破線枠 = 条件付きステップ（post=true 時のみ等） */
  optional?: boolean;
};

const BOX_X = 30;
const BOX_W = 620;
const BOX_H = 76;
const GAP = 26;
const TOP = 12;

/** 縦一列のシンプルなフロー図（エージェント各エンジン用の汎用版） */
export const FlowDiagram: React.FC<{ steps: FlowStep[]; ariaLabel: string }> = ({
  steps,
  ariaLabel,
}) => {
  const navigate = useNavigate();
  const goPrompt = (key: string) =>
    navigate(`${APP_ROUTER.AGENT.PROMPTS}?key=${encodeURIComponent(key)}`);

  const pitch = BOX_H + GAP;
  const boxY = (i: number) => TOP + i * pitch;
  const viewH = boxY(steps.length - 1) + BOX_H + 12;

  return (
    <svg
      viewBox={`0 0 680 ${viewH}`}
      width='100%'
      style={{ maxWidth: 680, fontFamily: 'inherit' }}
      role='img'
      aria-label={ariaLabel}
    >
      <defs>
        <marker
          id='agent-flow-arrow'
          markerWidth='10'
          markerHeight='10'
          refX='6'
          refY='3'
          orient='auto'
          markerUnits='strokeWidth'
        >
          <path d='M0,0 L6,3 L0,6 Z' fill='#8c8c8c' />
        </marker>
      </defs>

      {steps.slice(0, -1).map((_, i) => (
        <line
          key={`arr-${i}`}
          x1={BOX_X + BOX_W / 2}
          y1={boxY(i) + BOX_H}
          x2={BOX_X + BOX_W / 2}
          y2={boxY(i + 1) - 2}
          stroke='#8c8c8c'
          strokeWidth={1.5}
          markerEnd='url(#agent-flow-arrow)'
        />
      ))}

      {steps.map((s, i) => {
        const y = boxY(i);
        const c = FLOW_COLORS[s.kind];
        return (
          <g key={i}>
            <rect
              x={BOX_X}
              y={y}
              width={BOX_W}
              height={BOX_H}
              rx={8}
              fill={c.fill}
              stroke={c.stroke}
              strokeWidth={1.5}
              strokeDasharray={s.optional ? '6 4' : undefined}
            />
            <circle cx={BOX_X + 26} cy={y + BOX_H / 2} r={13} fill={c.stroke} />
            <text
              x={BOX_X + 26}
              y={y + BOX_H / 2 + 4}
              fontSize='13'
              fill='#fff'
              textAnchor='middle'
              fontWeight='bold'
            >
              {i + 1}
            </text>
            <text
              x={BOX_X + 50}
              y={y + 24}
              fontSize='14'
              fill={c.text}
              fontWeight='bold'
            >
              {s.title}
            </text>
            <text x={BOX_X + 50} y={y + 44} fontSize='11.5' fill='#595959'>
              {s.sub}
            </text>
            {s.promptKeys && (
              <text x={BOX_X + 50} y={y + 64} fontSize='12'>
                <tspan fill='#8c8c8c'>prompt: </tspan>
                {s.promptKeys.map((k, ki) => (
                  <tspan key={k}>
                    {ki > 0 && <tspan fill='#bfbfbf'> + </tspan>}
                    <tspan
                      fill='#1677ff'
                      style={{ cursor: 'pointer' }}
                      textDecoration='underline'
                      onClick={() => goPrompt(k)}
                    >
                      {k}
                    </tspan>
                  </tspan>
                ))}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export const FlowLegend: React.FC = () => (
  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
    <LegendItem color={FLOW_COLORS.llm.stroke} label='Anthropic（テキスト生成）' />
    <LegendItem color={FLOW_COLORS.search.stroke} label='Tavily（Web検索）' />
    <LegendItem color={FLOW_COLORS.local.stroke} label='ローカル処理（LLMなし）' />
    <LegendItem color={FLOW_COLORS.db.stroke} label='DB 保存' />
    <LegendItem color={FLOW_COLORS.deliver.stroke} label='配信（トーク投稿・push）' />
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: 3,
          border: '1.5px dashed #8c8c8c',
          display: 'inline-block',
        }}
      />
      <Text style={{ fontSize: 12 }}>破線 = 条件付き（post=true 時のみ 等）</Text>
    </span>
  </div>
);

const LegendItem: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span
      style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        background: color,
        display: 'inline-block',
      }}
    />
    <Text style={{ fontSize: 12 }}>{label}</Text>
  </span>
);
